import { ComponentChild, h } from 'preact';
import classnames from 'classnames';

import { links } from '../constants';

import { Title } from '../components/title';
import { ButtonCta } from '../components/button';
import { Panel } from '../components/Panel';
import { Footnote } from './Footnotes';

const IMAGES_DIR = `/assets/images/cards/`;
const DM_IMAGES_DIR = `/assets/images/cards/dm/`;

const unlimitedShared = {
    className: 'is-unlimited',
    selectedDays: [6, 7],
    details: [
        {
            copy: [
                <>
                    No upfront fees
                    <Footnote id="2" />
                </>,
            ],
            icon: 'piggy',
            iconWidth: 26,
            iconHeight: 22,
        },
        {
            copy: ['Flexible payments:', 'Buy days, weeks or months of insurance'],
            icon: 'coins',
            iconWidth: 26,
            iconHeight: 13,
        },
        {
            copy: [' Auto reload', 'at no extra cost'],
            icon: 'reload',
            iconWidth: 18,
            iconHeight: 24,
        },
        {
            copy: [
                'Coverage Extension:',
                'we’ll keep you covered and give you a few extra days to pay',
            ],
            icon: 'heart',
            iconWidth: 22,
            iconHeight: 20,
        },
        {
            copy: ['Control it from your phone,', 'make changes online'],
            icon: 'phone',
            iconWidth: 18,
            iconHeight: 24,
        },
        {
            copy: ['Instant', 'proof of insurance'],
            icon: 'zap',
            iconWidth: 15,
            iconHeight: 22,
        },
    ],
};

const plans = [
    {
        name: 'Unlimited Basic',
        description:
            'Great if you drive most days, and want liability coverage on a budget-friendly payment plan.',
        cardIcon: 'basic',
        ...unlimitedShared,
    },
    {
        name: 'Unlimited Full',
        description: 'Great if you want full coverage, on a budget-friendly payment plan.',
        cardIcon: 'full',
        ...unlimitedShared,
    },
];

type CardProps = {
    name: ComponentChild;
    description: string;
    cardIcon: string;
    details: {
        copy: ComponentChild[];
        icon: string;
        iconWidth: number;
        iconHeight: number;
    }[];
    className?: string;
};

const Card = ({ name, description, cardIcon, details, className }: CardProps) => (
    <div className={classnames('c-card', className)}>
        <div className="c-card__header">
            <div className="c-card__media">
                <picture>
                    <source
                        srcSet={`${IMAGES_DIR}${cardIcon}.svg`}
                        media={'(prefers-color-scheme: light)'}
                    />
                    <source
                        srcSet={`${DM_IMAGES_DIR}${cardIcon}.svg`}
                        media={'(prefers-color-scheme: dark)'}
                    />
                    <img
                        loading="lazy"
                        width="34"
                        height="34"
                        src={`${IMAGES_DIR}${cardIcon}.svg`}
                        alt=""
                    />
                </picture>
                <div>
                    <h3 className="c-card__title">{name}</h3>
                </div>
            </div>
            <p className="c-card__subtitle">{description}</p>
        </div>
        <div className="c-card__content">
            <Panel title="See details">
                <ul className="u-pt-1">
                    {details.map(({ copy, icon, iconWidth, iconHeight }, index) => (
                        <li key={index} className="u-mt-3 u-flex u-items-center">
                            <span className="u-flex-none u-mr-2 u-flex c-card__content-icon">
                                <picture>
                                    <source
                                        srcSet={`${IMAGES_DIR}${icon}.svg`}
                                        media={'(prefers-color-scheme: light)'}
                                    />
                                    <source
                                        srcSet={`${DM_IMAGES_DIR}${icon}.svg`}
                                        media={'(prefers-color-scheme: dark)'}
                                    />
                                    <img
                                        // make sure to keep loading property before src
                                        // otherwise lazy-load won't work
                                        // https://github.com/preactjs/preact-cli/issues/1799
                                        alt=""
                                        className="u-m-auto"
                                        height={iconHeight}
                                        loading="lazy"
                                        src={`${IMAGES_DIR}${icon}.svg`}
                                        width={iconWidth}
                                    />
                                </picture>
                            </span>
                            <span>
                                <b>{copy[0]}</b> {copy[1]}
                            </span>
                        </li>
                    ))}
                </ul>
            </Panel>
        </div>
    </div>
);

type Props = {
    className?: string;
    showCta?: boolean;
};

const PaymentPlans = ({ className, showCta }: Props) => {
    return (
        <section className={classnames('l-section', className)}>
            <div className="l-container">
                <div className="u-text-center">
                    <header>
                        <Title className="u-mb-8 md:u-mb-12">
                            <span>Payment plans</span>
                            <span>for every budget</span>
                        </Title>
                    </header>
                </div>

                <div className="c-plans__cards">
                    {plans.map((data, i) => (
                        <Card key={i} {...data} />
                    ))}
                </div>

                {showCta && (
                    <div className="c-plans__cta">
                        <ButtonCta icon href={links.QUOTE_FLOW}>
                            Check my price
                        </ButtonCta>
                        <div className="u-txt-gray-80 dark:u-txt-white u-text-center u-mt-4">
                            Sign up and get proof of insurance
                            <br /> in under 6 minutes
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default PaymentPlans;
